import React, {useCallback, useEffect, useMemo, useState} from "react";
import css from "./diversityFilter.module.scss";
import Slider from "rc-slider";
import classNames from "classnames";
import "rc-slider/assets/index.css";
import {ResetFilters} from "components";
import {
	TDiversitySelectionFilterProps,
	TDiversitySortValues,
} from "mapx-components/Filters/DiversitySelectionFilter/types";

function DiversitySelectionFilter(props: TDiversitySelectionFilterProps) {
	const {handleResetClick, genderDiversityImportance, ethnicDiversityImportance, setFilterParam} =
		props;

	const [genderValue, setGenderValue] = useState<number>(0);

	const [ethnicityValue, setEthnicityValue] = useState<number>(0);

	const sliderLabelByValue = (value: number) => {
		if (value === 33) {
			return "Low";
		} else if (value === 66) {
			return "Medium";
		} else if (value === 99) {
			return "High";
		} else {
			return "None";
		}
	};

	const genderTitle = useMemo(() => {
		return sliderLabelByValue(genderValue);
	}, [genderValue]);

	const ethnicityTitle = useMemo(() => {
		return sliderLabelByValue(ethnicityValue);
	}, [ethnicityValue]);

	const onFilterChanged = useCallback(
		(type: "gender" | "ethnicity" = "gender", value: number) => {
			const title = sliderLabelByValue(value);

			// If value is not changed handled
			if (type === "gender" && title !== genderDiversityImportance) {
				setFilterParam("gender_diversity_importance", title === "None" ? null : title);
			} else if (type === "ethnicity" && title !== ethnicDiversityImportance) {
				setFilterParam("ethnic_diversity_importance", title === "None" ? null : title);
			}
		},
		[genderDiversityImportance, ethnicDiversityImportance, setFilterParam],
	);

	const getSliderValueByTextValue = (textValue: TDiversitySortValues) => {
		switch (textValue) {
			case "High":
				return 99;
			case "Medium":
				return 66;
			case "Low":
				return 33;
			default:
				return 0;
		}
	};

	useEffect(() => {
		if (genderDiversityImportance) {
			const value = getSliderValueByTextValue(genderDiversityImportance);
			setGenderValue(value);
		} else {
			setGenderValue(0);
		}
	}, [genderDiversityImportance]);

	useEffect(() => {
		if (ethnicDiversityImportance) {
			const value = getSliderValueByTextValue(ethnicDiversityImportance);
			setEthnicityValue(value);
		} else {
			setEthnicityValue(0);
		}
	}, [ethnicDiversityImportance]);

	const resetClick = () => {
		setGenderValue(0);
		setEthnicityValue(0);
		handleResetClick();
	};

	return (
		<div className={css.diversityContainer}>
			<ResetFilters
				parentStyle={{color: "#5A5A5A", marginRight: 19}}
				onClick={resetClick}
				displayIcon={true}
			>
				Clear Selection
			</ResetFilters>

			<div className={css.rangeTitle}>Gender</div>
			<Slider
				dots
				key={"genderSlider"}
				className={classNames(css.slide, css.slideContainer)}
				step={33}
				max={99}
				value={genderValue}
				onChange={(value) => {
					setGenderValue(value as number);
					onFilterChanged("gender", value as number);
				}}
			/>

			<div className={css.rangeResult} data-testid="gender-title">
				{genderTitle || "None"}
			</div>

			<div className={css.rangeTitle}>Ethnicity</div>
			<Slider
				key={"ethnicitySlider"}
				className={classNames(css.slide, css.slideContainer)}
				step={33}
				max={99}
				value={ethnicityValue}
				onChange={(value) => {
					setEthnicityValue(value as number);
					onFilterChanged("ethnicity", value as number);
				}}
			/>

			<div className={css.rangeResult} data-testid="ethnicity-title">
				{ethnicityTitle || "None"}
			</div>
		</div>
	);
}

export default DiversitySelectionFilter;
